import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Trash2, Download } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { downloadSingle } from "@/lib/download";

// Full-screen viewer with navigation between items in a list: swipe left/right
// on touch devices, arrow keys or on-screen arrows on desktop. Used both by the
// public album (guests can download) and the dashboard moderation preview
// (which also gets an optional delete action).
export default function MediaLightbox({ items, index, onClose, onChange, onDelete, onDownload }) {
  const touchStartX = useRef(null);
  const [dragX, setDragX] = useState(0);
  const item = items[index];

  const goPrev = () => { if (index > 0) onChange(index - 1); };
  const goNext = () => { if (index < items.length - 1) onChange(index + 1); };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, items.length]);

  if (!item) return null;

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    setDragX(e.touches[0].clientX - touchStartX.current);
  };
  const onTouchEnd = () => {
    if (Math.abs(dragX) > 60) {
      if (dragX > 0) goPrev();
      else goNext();
    }
    setDragX(0);
    touchStartX.current = null;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="media-lightbox">
      <button className="absolute top-5 right-5 text-white/80 hover:text-white z-10" onClick={onClose} data-testid="lightbox-close-button">
        <X size={30} />
      </button>

      {onDelete && (
        <button
          className="absolute top-5 left-5 text-white/80 hover:text-rose-400 z-10 flex items-center gap-1.5 text-sm"
          onClick={(e) => { e.stopPropagation(); onDelete(item); }}
          data-testid="lightbox-delete-button"
        >
          <Trash2 size={20} /> Elimina
        </button>
      )}

      {index > 0 && (
        <button
          className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10 w-11 h-11 rounded-full items-center justify-center bg-white/10 backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          data-testid="lightbox-prev-button"
        >
          <ChevronLeft size={26} />
        </button>
      )}
      {index < items.length - 1 && (
        <button
          className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10 w-11 h-11 rounded-full items-center justify-center bg-white/10 backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          data-testid="lightbox-next-button"
        >
          <ChevronRight size={26} />
        </button>
      )}

      <div
        className="flex flex-col items-center max-w-full px-4 touch-pan-y"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ transform: dragX ? `translateX(${dragX * 0.4}px)` : undefined, transition: dragX ? "none" : "transform 0.2s ease" }}
      >
        {item.media_type === "video" ? (
          <video key={item.id} src={assetUrl(item.url)} controls autoPlay className="max-h-[80vh] max-w-full rounded-lg" data-testid="lightbox-video" />
        ) : (
          <img key={item.id} src={assetUrl(item.url)} alt={item.caption || ""} className="max-h-[80vh] max-w-full rounded-lg object-contain" />
        )}
        {(item.caption || item.uploaded_by_name) && (
          <div className="mt-4 text-center px-4">
            {item.caption && <p className="text-white text-base">{item.caption}</p>}
            {item.uploaded_by_name && <p className="text-white/60 text-sm mt-1">— {item.uploaded_by_name}</p>}
          </div>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); downloadSingle(item, index); onDownload?.(item); }}
          className="mt-4 flex items-center gap-2 text-sm text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition-colors duration-150"
          data-testid="lightbox-download-button"
        >
          <Download size={15} /> Scarica
        </button>
        <p className="text-white/40 text-xs mt-3 sm:hidden">{index + 1} / {items.length} · scorri per cambiare</p>
      </div>
    </div>
  );
}
